import React, { ChangeEventHandler } from "react"
import classNames from "classnames"
import tw, { styled } from "twin.macro"

import { Case, Default, If, Switch } from "@components/If"
import { css } from "@emotion/css"

interface CheckboxProps {
    /**
     * Id of Checkbox button
     */
    id?: string
    /**
     * Name of input checkbox
     */
    name: string
    /**
     * Value of the checkbox
     */
    value: string | string[] | number
    /**
     * Determine whether the Checkbox is checked
     */
    checked?: boolean
    /**
     * Determine whether the Checkbox is circle
     */
    circle?: boolean
    /**
     * className of the checkbox
     */
    className?: string
    /**
     * Disabled state of checkbox
     */
    disabled?: boolean
    /**
     * Trigger when the checked state is changing
     */
    onChange: ChangeEventHandler<HTMLInputElement>
    /**
     * The size of the checkbox
     */
    size?: "sm" | "md"
    /**
     * Icon displayed when checkbox is checked
     */
    dashIcon?: boolean
    /**
     * Determine whether the Checkbox is error
     */
    error?: boolean
    /**
     * Readonly state of checkbox
     */
    readonly?: boolean
}

interface StyledCheckBox {
    checked?: boolean
    circle?: boolean
    disabled?: boolean
    size?: string
    error?: boolean
    readonly?: boolean
}

const Wrapper = tw.div``

const StyledCheckbox = styled.div<StyledCheckBox>`
    ${tw`relative flex items-center justify-center cursor-pointer rounded border-[1.5px] border-main dark:border-dark-main hover:bg-teal-100 dark:hover:bg-dark-teal-100  hover:border-action-primary dark:hover:border-dark-action-primary `}
    ${({ circle }) => circle && tw`rounded-full`}
    ${({ checked }) =>
        checked &&
        tw`bg-foreground-teal dark:bg-dark-foreground-teal border-action-primary dark:border-dark-action-primary`}
    ${({ error }) => error && tw` border-error dark:border-dark-error`}
    ${({ disabled }) =>
        disabled &&
        tw`pointer-events-none !cursor-not-allowed border-main dark:border-dark-main bg-background-subtle dark:bg-dark-background-subtle`}
    ${({ size }) => {
        switch (size) {
            case "sm":
                return tw`w-4 h-4`
            case "md":
            default:
                return tw`w-5 h-5`
        }
    }}

    ${({ readonly }) => readonly && tw`pointer-events-none`}

    &:active {
        box-shadow: 0px 0px 0px 4px rgba(234, 250, 250, 0.5);
        ${tw`dark:shadow-[0px 0px 0px 4px rgba(24, 56, 57, 0.5)]`}
    }
`

const CheckboxLabel = styled.label``

const CheckboxInput = styled.input`
    ${tw`absolute h-0 w-0 opacity-0`}
`

const checkboxCss = css`
    color: #00b8be;

    &:hover {
        color: #00b8be !important;
    }
`

const Checkbox: React.FC<CheckboxProps> = ({
    id,
    name,
    value,
    checked,
    circle,
    className = "",
    disabled,
    onChange,
    size,
    dashIcon,
    error,
    readonly
}) => (
    <Wrapper className={classNames("reku-new", className)}>
        <CheckboxLabel>
            <StyledCheckbox
                checked={checked}
                error={error}
                circle={circle}
                disabled={disabled}
                aria-checked={checked}
                aria-disabled={disabled}
                role='checkbox'
                onChange={onChange}
                size={size}
                readonly={readonly}
            >
                <If condition={checked}>
                    <Switch>
                        <Case condition={dashIcon}>
                            <svg
                                width='12'
                                height='2'
                                viewBox='0 0 12 2'
                                fill='none'
                                xmlns='http://www.w3.org/2000/svg'
                                className={classNames(checkboxCss, {
                                    "!text-icon-disabled dark:!text-dark-icon-disabled": disabled,
                                    " !w-[14px] !h-[14px]": size === "md"
                                })}
                            >
                                <path
                                    d='M1.91666 1H10.0833'
                                    stroke='currentColor'
                                    strokeWidth='2'
                                    strokeLinecap='round'
                                    strokeLinejoin='round'
                                />
                            </svg>
                        </Case>
                        <Default>
                            <svg
                                width='12'
                                height='12'
                                viewBox='0 0 12 12'
                                fill='none'
                                xmlns='http://www.w3.org/2000/svg'
                                className={classNames(checkboxCss, {
                                    "!text-icon-disabled dark:!text-dark-icon-disabled": disabled,
                                    " !w-[14px] !h-[14px]": size === "md"
                                })}
                            >
                                <path
                                    d='M10 3L4.5 8.5L2 6'
                                    stroke='currentColor'
                                    strokeWidth='2'
                                    strokeLinecap='round'
                                    strokeLinejoin='round'
                                />
                            </svg>
                        </Default>
                    </Switch>
                </If>
            </StyledCheckbox>
            <CheckboxInput
                id={id}
                type='checkbox'
                checked={checked}
                disabled={disabled}
                onChange={onChange}
                value={value}
                name={name}
            />
        </CheckboxLabel>
    </Wrapper>
)

Checkbox.defaultProps = {
    id: undefined,
    checked: false,
    circle: false,
    className: "",
    disabled: false,
    size: "md",
    dashIcon: false,
    error: false,
    readonly: false
}

export default Checkbox
